/**
 * React auth context — Amazon Cognito username/password (MVP 1 dev).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  completeNewPassword as cognitoCompleteNewPassword,
  getCurrentCognitoUser,
  getIdToken,
  signInWithPassword,
  signOutCognito,
} from './CognitoClient';
import type { CognitoUser } from 'amazon-cognito-identity-js';

export interface AuthUser {
  cognitoUser: CognitoUser;
  /** `sub` from the ID token (set after first successful session read). */
  sub: string | null;
}

export interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  completeNewPassword: (user: CognitoUser, newPassword: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** ID token for debugging; prefer `api/client` which attaches Bearer automatically. */
  getIdToken: (forceRefresh?: boolean) => Promise<string | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function loadSub(user: CognitoUser): Promise<string | null> {
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session) => {
      if (err || !session?.isValid()) {
        resolve(null);
        return;
      }
      try {
        const payload = session.getIdToken().decodePayload() as { sub?: string };
        resolve(payload.sub ?? null);
      } catch {
        resolve(null);
      }
    });
  });
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    const cognitoUser = getCurrentCognitoUser();
    if (!cognitoUser) {
      setUser(null);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    void loadSub(cognitoUser).then((sub) => {
      if (cancelled) return;
      if (sub) {
        setUser({ cognitoUser, sub });
      } else {
        setUser(null);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    const session = await signInWithPassword(email, password);
    const payload = session.getIdToken().decodePayload() as { sub?: string };
    const cognitoUser = getCurrentCognitoUser();
    if (cognitoUser && payload.sub) {
      setUser({ cognitoUser, sub: payload.sub });
    }
  }, []);

  const completeNewPassword = useCallback(async (user: CognitoUser, newPassword: string) => {
    const session = await cognitoCompleteNewPassword(user, newPassword);
    const payload = session.getIdToken().decodePayload() as { sub?: string };
    if (payload.sub) {
      setUser({ cognitoUser: user, sub: payload.sub });
    }
  }, []);

  const signOut = useCallback(async () => {
    await signOutCognito();
    setUser(null);
  }, []);

  const tokenGetter = useCallback((forceRefresh?: boolean) => getIdToken(forceRefresh), []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      signInWithEmail,
      completeNewPassword,
      signOut,
      getIdToken: tokenGetter,
    }),
    [user, loading, signInWithEmail, completeNewPassword, signOut, tokenGetter],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth() must be called inside <AuthProvider>.');
  }
  return ctx;
}
